import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, ChevronLeft, ChevronRight, Tag, Loader2 } from 'lucide-react';
import { useProducts } from '../hooks/useApi';

const ProductsShowcase = () => {
  const { data: products, loading } = useProducts();
  const [index, setIndex] = useState(0);
  const visible = 3;
  const slides = Math.max(1, (products?.length || 0) - visible + 1);

  const next = () => setIndex((i) => (i + 1) % slides);
  const prev = () => setIndex((i) => (i - 1 + slides) % slides);

  useEffect(() => {
    if (!products || products.length === 0) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % slides), 6000);
    return () => clearInterval(t);
  }, [products, slides]);

  return (
    <section className="py-28 bg-[#050F2E] text-white relative overflow-hidden" data-testid="products-showcase-section">
      {/* BG accents */}
      <div className="absolute top-1/4 -left-32 h-96 w-96 rounded-full bg-[#FF4E50]/15 blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 -right-32 h-96 w-96 rounded-full bg-[#0066cc]/15 blur-3xl pointer-events-none" />

      <div className="container-pn relative">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3">Advance Printing</p>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight">Learn about our solutions</h2>
            <p className="mt-4 text-slate-300">
              Industry-leading spectrophotometers, color management tools, and proofing systems.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={prev}
              className="h-12 w-12 rounded-full border-2 border-white/15 hover:bg-white hover:text-[#0A2A63] flex items-center justify-center transition-colors"
              aria-label="Previous"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={next}
              className="h-12 w-12 rounded-full bg-[#FF4E50] text-[#050F2E] hover:bg-[#FF7273] flex items-center justify-center transition-colors"
              aria-label="Next"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Cards */}
        <div className="overflow-hidden">
          {loading || !products || products.length === 0 ? (
            <div className="text-center py-20 flex items-center justify-center gap-3 text-slate-300">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="font-semibold">Loading products...</span>
            </div>
          ) : (
          <div
            className="flex transition-transform duration-700 ease-out gap-6"
            style={{ transform: `translateX(calc(-${index} * (100% / 3 + 8px)))` }}
          >
            {products.map((p) => (
              <article
                key={p.id}
                className="min-w-[calc(100%/1)] md:min-w-[calc(100%/2-12px)] lg:min-w-[calc(100%/3-16px)] bg-white text-[#050F2E] rounded-3xl overflow-hidden shadow-2xl group flex flex-col"
              >
                <div className="relative h-64 bg-gradient-to-br from-slate-50 to-slate-100 p-6 flex items-center justify-center overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="max-h-full max-w-full object-contain transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-[#0A2A63] text-white px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest">
                    <Tag className="h-3 w-3" />
                    {p.code}
                  </div>
                  <div className="absolute top-4 right-4 bg-[#FF4E50] text-[#050F2E] px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest">
                    {p.brand}
                  </div>
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <h3 className="text-xl font-bold leading-tight mb-2">{p.name}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-5 flex-1">{p.shortDescription}</p>
                  <div className="flex items-end justify-between">
                    <div>
                      {p.price ? (
                        <>
                          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">From</p>
                          <p className="text-2xl font-black text-[#0A2A63]">${p.price.toLocaleString()}</p>
                        </>
                      ) : (
                        <p className="text-sm font-bold text-[#0A2A63]">{p.priceLabel || 'Contact us'}</p>
                      )}
                    </div>
                    <Link to={`/shop/${p.id}`}>
                      <Button size="sm" className="bg-[#0A2A63] hover:bg-[#061a3f] rounded-full font-bold btn-transition">
                        See product
                        <ArrowRight className="h-3.5 w-3.5 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
          )}
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-2 mt-10">
          {Array.from({ length: slides }).map((_, i) => (
            <button
              key={`slide-dot-${i}`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? 'bg-[#FF4E50] w-10' : 'bg-white/20 w-2 hover:bg-white/40'
              }`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductsShowcase;
